import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type Deployment } from '../lib/api'
import { ENV_LABEL } from '../lib/format'
import { StatusBadge, Tag, Spinner, EmptyState, ErrorNote } from '../components/ui/Primitives'

export default function Instances() {
  const [q, setQ] = useState('')
  const [env, setEnv] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['deployments'],
    queryFn: () => api.get<Deployment[]>('/deployments'),
    // Sin refetch agresivo: el estado en vivo llega por WebSocket en el detalle.
    refetchInterval: 15_000,
  })

  const filtered = useMemo(() => {
    if (!data) return []
    const needle = q.trim().toLowerCase()
    return data.filter((d) => {
      if (env && d.environment !== env) return false
      if (!needle) return true
      return (
        d.name.toLowerCase().includes(needle) ||
        d.app.slug.toLowerCase().includes(needle) ||
        d.app.name.toLowerCase().includes(needle) ||
        d.domains.some((x) => x.hostname.toLowerCase().includes(needle))
      )
    })
  }, [data, q, env])

  if (isLoading) return <div className="grid place-items-center py-20"><Spinner className="h-6 w-6" /></div>
  if (error) return <ErrorNote>No se pudieron cargar las instancias: {(error as Error).message}</ErrorNote>

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Instancias</h1>
          <p className="hint mt-0.5">{data?.length ?? 0} en total</p>
        </div>
        <div className="flex gap-2">
          <input
            className="input w-48"
            placeholder="Buscar…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select className="input w-40" value={env} onChange={(e) => setEnv(e.target.value)}>
            <option value="">Todos los entornos</option>
            <option value="PRODUCTION">Producción</option>
            <option value="STAGING">Staging</option>
            <option value="DEVELOPMENT">Desarrollo</option>
            <option value="PREVIEW">Preview</option>
          </select>
        </div>
      </header>

      {filtered.length === 0 ? (
        <EmptyState
          title={data?.length ? 'Ninguna instancia coincide con el filtro' : 'Todavía no hay instancias'}
          hint={
            data?.length
              ? undefined
              : 'Podés crear una nueva, o adoptar algo que ya esté corriendo en un servidor: el panel lo registra sin tocarlo.'
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((d) => (
            <Link key={d.id} to={`/instancias/${d.id}`} className="card block transition-colors hover:border-slate-600">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{d.app.slug} / {d.name}</p>
                  <p className="hint mt-0.5 truncate">
                    {ENV_LABEL[d.environment]} · {d.server.name} · {d.runtimeDriver.toLowerCase()}
                  </p>
                </div>
                <StatusBadge status={d.status} />
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {d.protected && <Tag tone="amber">protegida</Tag>}
                {d.adopted && <Tag tone="purple">adoptada</Tag>}
                {d.maintenanceMode && <Tag tone="blue">mantenimiento</Tag>}
                {d.port && <Tag>:{d.port}</Tag>}
              </div>

              {d.domains.length > 0 && (
                <p className="mt-3 truncate text-xs text-slate-500">
                  {d.domains.map((x) => x.hostname).join(' · ')}
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
